import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  FaBook, 
  FaClock, 
  FaUsers, 
  FaStar,
  FaCheckCircle,
  FaArrowRight,
  FaHeartbeat,
  FaStethoscope,
  FaMicroscope,
  FaDna,
  FaBrain,
  FaLungs,
  FaCalendarAlt,
  FaChartLine
} from 'react-icons/fa';

const PageContainer = styled.div`
  margin-top: 200px;
  min-height: calc(100vh - 210px);
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);


  @media (max-width: 768px) {
    margin-top: 120px;
  }
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, #0a4d68 0%, #088395 50%, #0a2b3c 100%);
  color: white;
  padding: 4rem 5%;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50px;
    right: -50px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(255, 217, 102, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -50px;
    left: -50px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(255, 217, 102, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1rem;
  position: relative;
  z-index: 2;
  
  span {
    color: #ffd966;
    display: block;
    font-size: 2rem;
    font-weight: 300;
    margin-top: 0.5rem;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.2rem;
  max-width: 700px;
  margin: 0 auto 2rem;
  opacity: 0.95;
  position: relative;
  z-index: 2;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 4rem 5%;
`;



const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(10, 77, 104, 0.05);
  
  .value {
    font-size: 2.2rem;
    font-weight: 700;
    color: #0a4d68;
    margin-bottom: 0.3rem;
  }
  
  .label {
    color: #5a6b7a;
    font-size: 0.9rem;
    text-transform: uppercase;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  color: #0a2b3c;
  margin-bottom: 2rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 80px;
    height: 4px;
    background: #ffd966;
    border-radius: 2px;
  }
`;

const Grid2Col = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ContentBlock = styled.div`
  p {
    color: #4a5f73;
    line-height: 1.8;
    margin-bottom: 1.5rem;
    font-size: 1.1rem;
  }
`;

const ImageWrapper = styled.div`
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  
  img {
    width: 100%;
    height: auto;
    display: block;
    transition: transform 0.5s ease;
    
    &:hover {
      transform: scale(1.05);
    }
  }
`;

const ChapterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin: 3rem 0;
  
  @media (max-width: 800px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ChapterCard = styled.div`
  background: #f8fafc;
  padding: 1rem;
  border-radius: 8px;
  text-align: center;
  border: 1px solid rgba(10, 77, 104, 0.05);
  
  .code {
    color: #0a4d68;
    font-weight: 700;
    margin-bottom: 0.3rem;
  }
  
  .desc {
    color: #5a6b7a;
    font-size: 0.8rem;
  }
`;

const Grid3Col = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin: 3rem 0;
  
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(10, 77, 104, 0.05);
  
  svg {
    font-size: 2rem;
    color: #ffd966;
    margin-bottom: 1rem;
  }
  
  h3 {
    color: #0a4d68;
    margin-bottom: 0.8rem;
  }
  
  p {
    color: #5a6b7a;
    font-size: 0.95rem;
    line-height: 1.6;
  }
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
`;

const ListItem = styled.li`
  margin-bottom: 0.8rem;
  color: #4a5f73;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  
  svg {
    color: #ffd966;
    font-size: 1rem;
  }
`;

const CTASection = styled.div`
  background: linear-gradient(135deg, #0a4d68, #088395);
  border-radius: 20px;
  padding: 3rem;
  text-align: center;
  color: white;
  margin-top: 4rem;
  
  h3 {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.1rem;
    margin-bottom: 2rem;
    opacity: 0.9;
  }
`;

const CTAButton = styled(Link)`
  background: #ffd966;
  color: #0a4d68;
  border: none;
  padding: 1rem 3rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  
  &:hover {
    background: white;
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
`;

const ICD10CM = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

 

  const chapters = [
    { code: "A00-B99", desc: "Infectious & Parasitic" },
    { code: "C00-D49", desc: "Neoplasms" },
    { code: "D50-D89", desc: "Blood & Immune" },
    { code: "E00-E89", desc: "Endocrine" },
    { code: "F01-F99", desc: "Mental Health" },
    { code: "G00-G99", desc: "Nervous System" },
    { code: "H00-H59", desc: "Eye" },
    { code: "H60-H95", desc: "Ear" },
    { code: "I00-I99", desc: "Circulatory" },
    { code: "J00-J99", desc: "Respiratory" },
    { code: "K00-K95", desc: "Digestive" },
    { code: "L00-L99", desc: "Skin" }
  ];

  return (
    <PageContainer>
      <HeroSection>
        <HeroTitle>
          ICD-10-CM Coding
          <span>Master Diagnosis Coding</span>
        </HeroTitle>
        <HeroDescription>
          Comprehensive training in ICD-10-CM diagnosis coding. Understand the purpose and structure of 
          the ICD-10-CM classification system and apply basic and fundamental coding rules. [citation:9]
        </HeroDescription>
      </HeroSection>

      <Container>
        

        <Grid2Col>
          <ContentBlock>
            <SectionTitle>Program Overview</SectionTitle>
            <p>
              The Clinical Coding Course aims to train professionals in the basic and fundamental rules 
              of clinical coding according to the ICD-10-CM standards. This course is designed to qualify 
              participants to perform clinical hospital coding activities. [citation:9]
            </p>
            <p>
              Learn to understand the purpose and structure of the ICD-10-CM/PCS classification system, 
              frame the importance of medical records in the clinical coding process, and recognize the 
              potential of the hospital morbidity database. [citation:9]
            </p>
          </ContentBlock>
          
          <ImageWrapper>
            <img 
              src="https://images.unsplash.com/photo-1581093458791-9a3c3a2c3e4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
              alt="ICD-10-CM Coding"
            />
          </ImageWrapper>
        </Grid2Col>

        <SectionTitle>ICD-10-CM Chapters</SectionTitle>
        <ChapterGrid>
          {chapters.map((chapter, index) => (
            <ChapterCard key={index}>
              <div className="code">{chapter.code}</div>
              <div className="desc">{chapter.desc}</div>
            </ChapterCard>
          ))}
        </ChapterGrid>

        <Grid3Col>
          <FeatureCard>
            <FaStethoscope />
            <h3>Official Guidelines</h3>
            <p>Master the official ICD-10-CM coding guidelines including chapter-specific guidelines and coding conventions.</p>
          </FeatureCard>
          
          <FeatureCard>
            <FaMicroscope />
            <h3>Pathophysiology</h3>
            <p>Understand disease processes to ensure accurate code selection and documentation requirements.</p>
          </FeatureCard>
          
          <FeatureCard>
            <FaDna />
            <h3>Specificity</h3>
            <p>Learn laterality, episode of care, and seventh character requirements for complete coding accuracy.</p>
          </FeatureCard>
        </Grid3Col>

        <CTASection>
          <h3>Ready to Master ICD-10-CM?</h3>
          <p>Join our next batch and become an expert diagnosis coder</p>
          <CTAButton to="/register">
            Enroll Now <FaArrowRight />
          </CTAButton>
        </CTASection>
      </Container>
    </PageContainer>
  );
};

export default ICD10CM;